import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router'
import { SessionUnavailable } from './SessionUnavailable'
import { useReturnTo } from '../../auth/returnTo'
import { Button } from '../../platform/primitives/Button'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { Field } from '../../platform/primitives/Field'
import { emit } from '../../platform/analytics/analytics'
import { MFA_CHALLENGE_EXPIRED, authErrorFor } from '../../auth/errors'
import { AuthFailure, type MfaFactor } from '../../auth/port'
import { labelFor } from '../../auth/factorLabel'
import { useAuth } from '../../auth/useAuth'
import type { FieldError } from '../../auth/errors'
import { AuthCard } from './AuthCard'
import { Lock } from 'lucide-react'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * Both factors this screen can present are six digits: Firebase's SMS codes are
 * six, and TOTP is six by RFC 6238's default, which every authenticator ships.
 *
 * Named because two things have to agree — the sanitiser's slice and the length
 * Verify waits for. A slice of 6 against a Verify waiting for 8 is a submit
 * button that never enables, and nothing on screen would say why.
 *
 * The input carries no `maxLength`; see the field for why that attribute breaks
 * paste rather than helping it.
 */
const CODE_LENGTH = 6

/**
 * What the code box keeps out of whatever arrives — a keystroke or a whole SMS.
 *
 * **A run of EXACTLY six digits, not the first six digits.** The obvious
 * `replace(/\D/g,'').slice(0,6)` is wrong for this product specifically, and a
 * test caught it: our brand is *TrackE5*, so the message "Your TrackE5 code is
 * 123456" carries a stray `5` before the code and the naive filter yields
 * `512345` — six plausible digits, uniformly wrong, and wrong in a way a user
 * would read as the code being rejected. Splitting on non-digits finds the group
 * that IS a code rather than the first digits that happen to appear.
 *
 * The fallback still strips and truncates, which is what makes typing work:
 * mid-entry there is no six-run yet, and `12ab34` has to become `1234`.
 *
 * `split(/\D+/)` rather than a lookbehind — same result, and it does not depend
 * on a regex feature Safari only reached in 16.4.
 */
function sixDigits(raw: string): string {
  const run = raw.split(/\D+/).find((part) => part.length === CODE_LENGTH)
  return run ?? raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
}

/**
 * M1-R5, Firebase-sourced factors only.
 *
 * The account's second factor is cleared here and nowhere else. Two things
 * about this screen are decisions rather than mechanics:
 *
 * **A wrong code does not drop the challenge.** `submit` rejects and the state
 * stays `mfaRequired`, so the user types the next code into the same box. A
 * challenge cleared on the first mistake is a sign-in loop, and TOTP codes
 * rotate every 30 seconds — a mistyped digit is the ordinary case.
 *
 * **`signin_completed` is emitted here**, with `mfa_used: true`. `SignIn`
 * deliberately withholds it on a challenge because at that moment no session
 * exists; if this screen did not emit it, every challenged sign-in would be
 * missing from the completion funnel rather than merely flagged.
 */
export function MfaChallenge() {
  const auth = useAuth()
  const challenge = auth.status === 'mfaRequired' ? auth.challenge : null
  const factors = challenge?.factors ?? []
  // `unpresentable` is deliberately NOT hoisted alongside `factors`. It has one
  // reader, the analytics effect below, and it is read off `challenge` there —
  // see issue 218: as a `challenge?.x ?? []` const it was a fresh array on every
  // render, which gave that effect a dependency list that could never match.
  // `factors` stays because the render body uses it in five places.

  /**
   * Consumed only once a session actually exists, exactly as
   * `RedirectWhenSignedIn` does it.
   *
   * A hook, and unconditional, because `takeReturnTo` REMOVES the key it reads:
   * calling it in a render body makes the render impure and `<StrictMode>`'s
   * double invoke throws away the destination it just took. `returnTo.ts`'s
   * header is the record of that costing the feature twice.
   */
  const destination = useReturnTo(auth.status === 'signedIn')

  // `undefined` means "not chosen yet", which for a single-factor challenge is
  // the same as choosing the only one. Held as an id rather than an index so a
  // re-ordered list cannot silently repoint it.
  const [chosen, setChosen] = useState<string | null>(null)
  const selected = factors.find((f) => f.id === chosen) ?? factors[0]

  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * The challenge died of old age, and no control on this screen can revive it.
   *
   * A state rather than another `error`, because it is the one auth failure here
   * that is TERMINAL. This component's header argues that a wrong code must not
   * drop the challenge — right, and the reason is that a TOTP code rotates every
   * 30 seconds so a mistyped digit is the ordinary case. That reasoning does not
   * extend to this: the pending credential behind every retry has expired, so
   * the code box, Verify, and Send a new code would all fail identically and
   * forever. Leaving them on screen under a red sentence is a trap — the user
   * reads "try again", tries again, and gets the same message.
   *
   * Set from BOTH paths. `prepare` replays the same credential `submit` does, so
   * a send made after the window closes fails the same way.
   */
  const [expired, setExpired] = useState(false)

  /**
   * The six digits, held rather than read off the form at submit.
   *
   * Controlled because the input now SANITISES — an SMS pasted whole
   * ("Your TrackE5 code is 123456") has to become `123456`, and a stripped value
   * that the DOM still holds unstripped is two sources of truth. See `.auth-otp`
   * in auth.css for the rest of the treatment.
   */
  const [code, setCode] = useState('')

  /**
   * The re-render trigger for a send, and nothing more.
   *
   * Per factor rather than a single boolean: an account with an authenticator
   * AND a phone can switch between them, and "sent" is a fact about the phone,
   * not about the screen. It is NOT the source of truth — `sent()` below ORs it
   * with the port, which is.
   */
  const [delivered, setDelivered] = useState<ReadonlySet<string>>(new Set())

  /**
   * Has this factor's code been brought into existence?
   *
   * **The port is the source of truth and is asked every render.** The local
   * set exists only to force a re-render after a send — it is an OR, not a
   * cache, so the two cannot disagree. Seeding local state from the port on
   * mount does not work and is worth recording: the challenge arrives
   * asynchronously through `completeRedirect`, so at first render it is still
   * null and a `useState` initialiser would seed from nothing and never re-run.
   *
   * What this buys is the remount case — one browser Back, now that the bare
   * routes bounce a challenged user here. Without it the screen offers to send
   * a code the user is already holding and spends a second SMS on it.
   */
  const sent = (factor: MfaFactor) =>
    delivered.has(factor.id) || challenge?.prepared(factor.id) === true

  /**
   * Where the reCAPTCHA widget mounts.
   *
   * Rendered by this component and handed to the port, rather than left for the
   * adapter to append somewhere. Invisible reCAPTCHA is still a widget that can
   * appear, challenge the user and fail — see `MfaChallenge.prepare`. A div
   * owned by the screen is one a test can find and a layout can account for.
   */
  const recaptcha = useRef<HTMLDivElement>(null)

  /**
   * Where focus must go once an action settles, and it is a REQUEST that is
   * consumed — not a flag that lingers.
   *
   * Every control on this screen disables itself while in flight, and a browser
   * blurs a disabled element to `<body>`. So every send moved a keyboard user
   * to the top of the document: the successful FIRST send only looked fine
   * because a freshly-mounted code box happened to autofocus. A resend, and any
   * failed send, dropped focus on the floor.
   *
   * The earlier `focusCode` boolean was cleared by the input's own `onFocus`,
   * which never fires on a resend because the input is already mounted and
   * already focused. It stayed true and stole focus from the radio group on the
   * next factor switch — the same click producing opposite behaviour depending
   * on whether a resend had happened. web-tester caught that.
   *
   * An effect consumes this and clears it unconditionally, so nothing survives
   * into a later render.
   */
  const [focusRequest, setFocusRequest] = useState<'code' | 'action' | null>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  const actionRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (focusRequest === null) return
    // Cleared FIRST and unconditionally: a request that cannot be honoured —
    // the target unmounted — must not survive to hijack a later render.
    setFocusRequest(null)
    const target = focusRequest === 'code' ? codeRef.current : actionRef.current
    target?.focus()
  }, [focusRequest])

  /**
   * The live region's text is DERIVED from the selected factor; this only
   * blanks it for a moment so that a repeat announcement is a real change.
   *
   * Holding the sentence in state instead was a regression, and it produced
   * three faults at once: the region announced a code sent to 4417 while the
   * button offered to send one to 9002, a factor round trip re-inserted the
   * region already containing text — the exact insertion-with-content shape
   * this was built to prevent — and a remount with a code already sent showed
   * nothing at all. web-tester found all three.
   *
   * Derived text restores the tie to the factor. The blank restores the resend
   * announcement: a live region announces a CHANGE, and re-setting an identical
   * string changes nothing.
   */
  const [blank, setBlank] = useState(false)
  const reannounce = useRef<number | undefined>(undefined)
  useEffect(() => () => window.clearTimeout(reannounce.current), [])

  /**
   * The selection as it is NOW, readable from an async closure.
   *
   * `send` and `onSubmit` capture `selected` when they start, and an SMS can be
   * in flight for seconds. Switching factor meanwhile put the first factor's
   * failure on the second one's screen: "too many codes today" beside a Send
   * code button for a number nothing had been sent to. `setError(null)` on the
   * switch cannot help — it runs first, and the failure lands after it.
   *
   * A ref rather than state: this is read during an async continuation, not
   * rendered, and it must not itself cause a render.
   */
  const selectedNow = useRef<string | undefined>(undefined)
  selectedNow.current = selected?.id

  /**
   * The picker's rows are targets too.
   *
   * A native radio plus a 13.5px label is ~20px, under the 33px page target and
   * well under M0-R29's 44px on a phone — where this screen mostly arrives,
   * since M1-R23 puts social sign-in on the redirect path. `useHitTarget`, not
   * `HIT_TARGET.page`, for the reason `Field` gives: the constant is flat 33 and
   * cannot rise. Inline because it is a floor a stylesheet must not undercut,
   * which is the one thing CLAUDE.md admits inline.
   */
  const rowHeight = useHitTarget('page')

  /**
   * `mfa_challenge_shown` is about the challenge, not about the render.
   *
   * Keyed on the challenge object and guarded by a ref: without it, every
   * keystroke in the code box re-renders this screen and an effect with no deps
   * discipline would count typing as challenges.
   */
  const announced = useRef<object | null>(null)
  useEffect(() => {
    if (challenge === null || announced.current === challenge) return
    announced.current = challenge
    // Read off `challenge` rather than closing over the `factors` and
    // `unpresentable` consts above — issue 218. Those are `challenge?.x ?? []`,
    // so on every render where `challenge` is null they are a FRESH array, and
    // listing them as dependencies gave this effect a dep list that could never
    // match. It was harmless only because the ref guard above already made the
    // body idempotent; the guard was carrying the correctness, and the dep list
    // was decoration. Both fields are required on the port type, so there is
    // nothing to default here.
    // Named apart from the outer `factors`, which the render body uses. Same
    // value; shadowing it inside a 400-line component reads as a bug that is not
    // there. web-architect's note.
    const { factors: challengeFactors, unpresentable } = challenge
    if (challengeFactors.length === 0) {
      // The KIND we could not present, where the first version sent a flat
      // `unavailable` for every case. That series was documented as "how often
      // an account reaches a challenge this client cannot present", which would
      // have funded backend-sourced MFA off a count that is almost entirely SMS
      // users — the one factor a port change, not a new backend, would fix.
      const kind = unpresentable[0] ?? 'unavailable'
      emit('mfa_challenge_shown', { factor_type: kind })
      emit('mfa_challenge_result', {
        factor_type: kind,
        result_status: 'unsupported',
      })
      return
    }
    emit('mfa_challenge_shown', { factor_type: challengeFactors[0]?.kind })
  }, [challenge])

  // `unknown` is not `signedOut`, and the difference is this screen's whole
  // arrival path: a redirect challenge is read out of `getRedirectResult`
  // asynchronously, so for the first frames there is neither a user nor a
  // challenge. Bouncing there would throw every phone sign-in back to /signin.
  if (auth.status === 'unknown') {
    return (
      <AuthCard
        icon={
          <span className="auth-icon">
            <Lock size={24} strokeWidth={1.9} aria-hidden="true" />
          </span>
        }
        title="Two-factor check"
      >
        <p className="auth-card__note mfa-status" role="status">
          Checking your sign-in…
        </p>
      </AuthCard>
    )
  }

  // Reached by typing the URL. There is nothing to render, and a blank screen
  // is worse than a bounce.
  if (auth.status === 'signedOut') return <Navigate to="/signin" replace />

  // Firebase has reported the user and the session POST has not answered yet.
  // NOTHING, not a navigation: `RequireAuth` states the same rule for the same
  // reason — it is the state before there is an answer, and acting on it costs
  // a second navigation when the POST then fails.
  if (auth.status === 'bootstrapping') {
    return (
      <AuthCard title="Two-factor check">
        <p className="auth-card__note mfa-status" role="status">
          Signing you in…
        </p>
      </AuthCard>
    )
  }

  // Authenticated, no app session. The same situation `RequireAuth` renders
  // this screen for, and it is a bare-layout screen already — a blank page here
  // would strand a user whose factor has just been accepted.
  if (auth.status === 'sessionUnavailable') return <SessionUnavailable />

  // The factor cleared. To the DESTINATION, not to `/`.
  //
  // `/` was the defect web-architect caught: it fires while the deep link is
  // still in storage, so `/transactions?page=2` → challenged → passed would
  // land on Overview and leave `RETURN_TO_KEY` behind to hijack the next
  // sign-in. M1-R7, broken by the screen that sits between the user and it.
  //
  // Task 15 wraps the BARE routes in `RedirectWhenSignedIn`, which does exactly
  // this and would then own it — that guard returns `<Navigate>` instead of the
  // outlet, so this component does not render at all and cannot double-consume.
  // Correct before and after, rather than correct only afterwards.
  if (challenge === null) return <Navigate to={destination} replace />

  /**
   * The way out, and it belongs on EVERY state of this screen.
   *
   * It used to render only for an empty factor list. Both review agents called
   * that a trap independently, and they were right: once the bare routes bounce
   * a challenged user back here (`RedirectWhenSignedIn`), /signin, /signup and
   * /forgot-password all return to this screen, and a user whose code never
   * arrives — wrong number, lost phone, no signal — has no control that leaves.
   * A full reload does escape, because Firebase mints no user while a challenge
   * is outstanding, but "press F5" is not a recovery path and nothing said so.
   *
   * Signs out rather than navigating. Leaving the challenge in place would put
   * the user back here the moment anything re-read the state, and the sign-in
   * form cannot start a fresh attempt against a stale challenge.
   *
   * **Never disabled by `busy`.** It is the escape hatch, and the state most
   * worth escaping is a `prepare` that never settles — reCAPTCHA can hang, and
   * with a disabled exit that is a locked screen.
   */
  const wayOut = (variant: 'primary' | 'outline') => (
    <Button variant={variant} size="form" onClick={() => void auth.signOut()}>
      Back to sign in
    </Button>
  )

  /**
   * The challenge expired. Nothing on this screen can finish it — see `expired`.
   *
   * The whole card is replaced rather than a message added to it, and that is
   * the point: leaving the code box and Verify on screen under this sentence
   * would offer the user two controls that are now guaranteed to fail. The one
   * action that works is the first factor again, so it is the only one drawn.
   *
   * `FormError` rather than a bare `<p>`: the sentence is an auth failure and
   * gets the failure skin and the `role="alert"` every other one gets — one
   * place decides how these look. `authErrorFor` is called rather than the
   * message inlined, so the copy lives beside the code it belongs to.
   *
   * `signOut`, not a `<Navigate>`: the dead challenge is still in the state, and
   * navigating would leave it there for the next thing that reads it to put the
   * user straight back here. It is what `wayOut` does, under the label this
   * situation calls for.
   */
  if (expired) {
    return (
      <AuthCard title="Two-factor check">
        <FormError error={authErrorFor(MFA_CHALLENGE_EXPIRED)} />
        <Button variant="primary" size="form" onClick={() => void auth.signOut()}>
          Sign in again
        </Button>
      </AuthCard>
    )
  }

  /**
   * The account has a second factor this client cannot present.
   *
   * **The copy no longer names SMS.** It used to, on the grounds that a phone
   * factor was the one filtered kind and so the only reachable cause. #171
   * made phone presentable, so the adapter filters nothing and that sentence
   * became both unreachable and untrue — the app can check a text message now.
   * `unpresentable` stays on the port as the obligation on a future filtering
   * adapter; what is gone is a specific claim no adapter can currently justify.
   *
   * **"Let them in" is not implementable**, which is where the plan's
   * instruction for this branch ends up. Firebase mints no ID token while a
   * challenge is outstanding, so there is no credential for a session POST, and
   * `authMachine` leaves `mfaRequired` only via `reported` or `cleared`. What
   * makes that acceptable rather than a lock-out: the backend enforces no MFA
   * at all (`api/dependencies.py:107` logs `sign_in_second_factor` and never
   * rejects), so an iOS user whose only factor is biometric or push signs in
   * here normally and never sees this screen.
   */
  if (factors.length === 0) {
    return (
      <AuthCard title="Two-factor check">
        <p className="auth-card__note mfa-status" role="status">
          Your account is protected by a second factor this browser can&apos;t check. Sign in from
          the TrackE5 iPhone app instead.
        </p>
        {wayOut('primary')}
      </AuthCard>
    )
  }

  /** What the region says about a delivered code. One wording, one place. */
  function textFor(factor: MfaFactor): string {
    return `We sent a code to ${factor.hint ?? 'your phone'}.`
  }

  /**
   * Blank the region, then let the derived text return a tick later.
   *
   * The gap is the whole point on a RESEND, where the sentence is identical and
   * React would otherwise write nothing to the DOM at all — no content change,
   * no announcement, and the one control that exists because the first message
   * never arrived confirms nothing.
   */
  function announce() {
    window.clearTimeout(reannounce.current)
    setBlank(true)
    reannounce.current = window.setTimeout(() => setBlank(false), 120)
  }

  /**
   * Ask the port to bring the code into existence — for SMS, send it.
   *
   * A failure here is NOT a wrong code, and it is reported as its own thing:
   * `result_status: 'undelivered'` rather than `failed`. Folding the two
   * together would put "user typed the wrong digits" and "we never sent the
   * message" in one number, and they have opposite fixes.
   */
  async function send(factor: MfaFactor) {
    if (challenge === null) return
    const container = recaptcha.current
    // Says something rather than nothing. A silent return leaves a button that
    // visibly does not work, and this is the one failure the container argument
    // exists to prevent — it should not be the one that fails invisibly.
    //
    // **Untested, and unreachable while the div renders unconditionally**, which
    // is the same reason. web-tester confirmed no mutation of this branch can
    // redden the suite. Kept rather than deleted because the ref contract is one
    // refactor away from being broken by someone who conditions that div on
    // something, and a reported failure is what they would need.
    if (container === null) {
      setError(authErrorFor('unexpected'))
      return
    }

    setError(null)
    setBusy(true)
    try {
      await challenge.prepare(factor.id, container)
      // Recorded whatever the user is looking at now: the code really was sent
      // for THIS factor, and the set is keyed by factor.
      setDelivered((seen) => new Set(seen).add(factor.id))
      // The rest is about the screen, so it only applies if the screen is still
      // showing the factor this send was for.
      if (selectedNow.current !== factor.id) return
      // Whatever is in the box belongs to the previous message. A resend exists
      // because the first code did not arrive or did not work, so keeping those
      // digits offers the user a value that is now certainly wrong.
      setCode('')
      // The code box is where the user has to go next, on a first send and on a
      // resend alike. Without this the button they pressed disables, the browser
      // blurs it to <body>, and a keyboard user is at the top of the document.
      setFocusRequest('code')
      announce()
    } catch (thrown) {
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      // Measured all the same — the send failed regardless of where the user
      // has navigated since, and dropping it would understate delivery failures.
      emit('mfa_challenge_result', {
        factor_type: factor.kind,
        result_status: 'undelivered',
      })
      // Before the selection guard below, deliberately: what expired is the
      // half-finished sign-in, not this factor's send, so it is dead whichever
      // factor the user has switched to while the SMS was in flight.
      if (detail.code === MFA_CHALLENGE_EXPIRED) {
        setExpired(true)
        return
      }
      if (selectedNow.current !== factor.id) return
      setError(detail)
      // Back to the control they pressed, which re-enables as `busy` clears.
      // There is no code box on this path for focus to land in, so without this
      // the user is at <body> with an error they have to go and find.
      setFocusRequest('action')
    } finally {
      setBusy(false)
    }
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    // `code` state rather than `new FormData(event.currentTarget)`: the input
    // sanitises to digits, and reading the DOM back would reintroduce the raw
    // value the sanitiser exists to remove.
    if (challenge === null || selected === undefined) return

    setError(null)
    setBusy(true)
    try {
      await challenge.submit(selected.id, code)
      emit('mfa_challenge_result', {
        factor_type: selected.kind,
        result_status: 'passed',
      })
      // The half `SignIn` withheld. `method` is deliberately absent rather than
      // guessed: the challenge does not carry which credential raised it, and
      // vocabulary.ts admits an absent value as "not measured" — which is true
      // — where a fabricated 'email' would be a number that is simply wrong.
      emit('signin_completed', { mfa_used: true })
      // No navigation here. Firebase reports the user, the provider dispatches
      // `reported`, and this component's own `challenge === null` branch takes
      // over — one place decides where a cleared challenge goes.
    } catch (thrown) {
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      emit('mfa_challenge_result', {
        factor_type: selected.kind,
        result_status: 'failed',
      })
      // Not an error ON this form — there is no form left. See `expired`.
      if (detail.code === MFA_CHALLENGE_EXPIRED) {
        setExpired(true)
        return
      }
      setError(detail)
      // The same disabled-blur that cost focus on the send paths, on the path
      // this screen's own header calls the ordinary case: a TOTP code rotates
      // every 30 seconds, so a mistyped digit is the normal outcome, not the
      // rare one. Verify disables itself, the browser moves focus to <body>,
      // and the error is `Field`'s own `role="alert"` with no tabindex — so a
      // keyboard user ends up at the top of the document with a message to go
      // and find. Back to the box they have to correct.
      setFocusRequest('code')
    } finally {
      setBusy(false)
    }
  }

  // Nothing to type yet: this factor's code has to be sent, and has not been.
  // Drives the copy AND which control the screen offers, so the two cannot
  // disagree — a Send code button under "enter the code we sent" is the bug
  // this single expression exists to make unrepresentable.
  const awaiting = selected !== undefined && selected.needsDelivery && !sent(selected)

  /**
   * The design draws ONE authenticator prompt — no factor picker — so its
   * `align-items:center; text-align:center` is specified for a simpler screen
   * than the one we ship. Centring a radio group detaches each label from its
   * control, so the modifier follows the picker rather than the screen.
   */
  const single = factors.length === 1

  /* The design's sentence, and it is a subtitle now rather than a note in the
     body: `AuthCard` puts it directly under the h1 at the 5px group gap, which
     is where the source has it. Ours stays parameterised by factor, because
     the design only ever draws the authenticator wording. */
  const prompt = single
    ? awaiting
      ? `We'll text a code to ${(factors[0] as MfaFactor).hint ?? 'your phone'}.`
      : `Enter the 6-digit code from your ${labelFor(factors[0] as MfaFactor).toLowerCase()}.`
    : undefined

  return (
    <AuthCard title="Two-factor check" subtitle={prompt} centered={single}>
      {single ? null : (
        // A radio group only where there is a choice: a group of one is a
        // control that cannot change anything.
        <fieldset className="auth-choice">
          <legend className="auth-choice__legend">Where should we check?</legend>
          {factors.map((factor) => (
            <label key={factor.id} className="auth-choice__option" style={{ minHeight: rowHeight }}>
              <input
                type="radio"
                name="factor"
                value={factor.id}
                checked={selected?.id === factor.id}
                onChange={() => {
                  setChosen(factor.id)
                  // A pending blank belongs to the send that requested it, not
                  // to whatever the user selects next.
                  window.clearTimeout(reannounce.current)
                  setBlank(false)
                  // The previous factor's failure is not this one's. A
                  // quota-exceeded message says "use your authenticator app if
                  // you have one" — and without this it stayed on screen above
                  // the authenticator's own code box, telling a user who did
                  // exactly that to do it again. `send` and `onSubmit` both
                  // clear; only this one did not, so only `form`-level errors
                  // survived, which is all three delivery codes.
                  setError(null)
                  // Nor is it this factor's code. A TOTP code typed for the
                  // authenticator is not the SMS code, and submitting it
                  // against the phone factor spends one of a short-lived
                  // challenge's few attempts on a value that cannot be right.
                  setCode('')
                }}
              />
              {labelFor(factor)}
            </label>
          ))}
        </fieldset>
      )}

      {/* The send is otherwise silent to a screen reader: the code box appears
          and the note's wording changes, but neither is announced, so someone
          not looking at the screen has no signal that the message went.

          **Mounted empty, filled in later, and the SAME element throughout.** A
          live region has to exist before its content changes for the change to
          be announced reliably; inserting the region and its text in one commit
          is the case assistive tech handles worst, and NVDA and JAWS have both
          historically missed it. Empty, it announces nothing — there is no
          content change — so mounting it early costs no false announcement.

          Mounted whenever the CHALLENGE has any factor that can receive a code
          — not whenever the SELECTED one can. Keying it to the selection
          unmounted the region on a switch to the authenticator and remounted it
          already containing text on the way back: insertion-with-content, three
          clicks away for any two-factor user, re-announcing a send that had
          already happened. A TOTP-only challenge still never renders it. No `:empty` rule, unlike
          the reCAPTCHA host below: `display: none` removes an element from the
          accessibility tree, so a hidden live region is not a live region and
          filling it would be an insertion again. It costs 16px of the card's
          flex gap while empty, and that is the trade. See auth.css. */}
      {factors.some((f) => f.needsDelivery) && (
        <p className="auth-card__note mfa-status" role="status">
          {blank || selected === undefined || !sent(selected) ? '' : textFor(selected)}
        </p>
      )}

      {awaiting ? (
        // No code box at all until a message exists. Rendering one disabled
        // would still read as "type here", and the whole reason phone was
        // unsupported until #171 was that a box in front of an unsent message
        // is worse than saying nothing.
        <>
          <FormError error={error} />
          <Button
            variant="primary"
            size="form"
            disabled={busy}
            ref={actionRef}
            onClick={() => void send(selected as MfaFactor)}
          >
            {busy ? 'Sending…' : 'Send code'}
          </Button>
        </>
      ) : (
        <form className="auth-form" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field
            size="form"
            // The one-time-code treatment: centred, spaced, six cells wide. The
            // skin is in auth.css — an inline style here could not be given the
            // `:focus-visible` the field already owns.
            className="auth-otp"
            label="Verification code"
            name="code"
            // `one-time-code` is what lets iOS and Chrome offer the code from the
            // SMS or the authenticator. `inputMode` gets the numeric keypad
            // without `type="number"`, which would strip a leading zero.
            autoComplete="one-time-code"
            inputMode="numeric"
            /**
             * `[0-9]*`, not `[0-9]{6}`. The form is `noValidate`, so the browser
             * enforces neither — what this attribute still does is tell iOS to
             * show the digits-only keypad rather than the full keyboard.
             * `maxLength` is the real cap, and `onChange` is the real filter.
             */
            pattern="[0-9]*"
            /**
             * **No `maxLength`, and its absence is the fix rather than an
             * oversight.** `maxLength={6}` truncates a PASTE to six characters
             * before any handler sees it, so "Your TrackE5 code is 123456"
             * arrived as "Your T" and the digit filter below reduced that to
             * the empty string — pasting an SMS silently cleared the box. The
             * cap lives in `onChange` instead, where it applies to the digits
             * rather than to the raw text. `value` is controlled and never
             * exceeds `CODE_LENGTH`, so nothing is lost by dropping it.
             */
            value={code}
            /**
             * See `sixDigits`. Not defensive tidying — it is what makes a
             * pasted SMS work: the whole message arrives from a phone's
             * clipboard, and every character that is not the code is noise the
             * server would reject as a wrong one.
             */
            onChange={(e) => setCode(sixDigits(e.target.value))}
            error={error?.field === 'code' ? error.message : undefined}
            // Focus is moved by the effect above, never by `autoFocus`. A plain
            // TOTP arrival must not steal focus from a user who has already
            // tabbed somewhere, and only a send can produce the request.
            ref={codeRef}
          />
          <FormError error={error} />
          {/* Held until the code is complete.

              Standard for a one-time code, and it earns its keep here for a
              reason this screen learned the hard way: the challenge behind
              Verify expires in minutes (`MFA_CHALLENGE_EXPIRED`), so a round
              trip spent on four digits is a real cost, not just a wasted
              request. The input is digits-only and capped at six, so the
              enabling condition is always reachable — this cannot strand
              anyone — and the subtitle already says how many to expect. */}
          <Button
            type="submit"
            variant="primary"
            size="form"
            disabled={busy || code.length !== CODE_LENGTH}
          >
            {busy ? 'Verifying…' : 'Verify'}
          </Button>
          {/* Only where a code can be re-sent. An authenticator app has no
              "send again" — the code is already rotating on the user's screen,
              and offering it would be a button that does nothing.

              `outline`, not a second `primary`: CLAUDE.md allows one lime
              surface per screen and Verify is it. */}
          {selected?.needsDelivery === true && (
            <Button
              type="button"
              variant="outline"
              size="form"
              disabled={busy}
              ref={actionRef}
              onClick={() => void send(selected)}
            >
              {busy ? 'Sending…' : 'Send a new code'}
            </Button>
          )}
        </form>
      )}

      {/* Every state of this screen can be left. See `wayOut`. `outline`,
          because Verify or Send code is already this screen's one lime surface. */}
      {wayOut('outline')}

      {/* The reCAPTCHA container. Empty until `prepare` mounts a widget into
          it, and present in the DOM before that call rather than conditionally
          — the ref has to be attached at the moment the button is pressed.

          Classed rather than bare: `.auth-card` is a flex column with a 16px
          gap, which applies between adjacent items whatever their height, so an
          always-empty div added 16px of dead space to the bottom of every
          two-step screen — including TOTP-only, where it can never be used.
          `:empty` stops applying the moment the widget mounts. */}
      <div className="mfa-recaptcha" ref={recaptcha} />
    </AuthCard>
  )
}
